// @ts-check
import { useEffect, useState } from "react";

import { WidgetState } from "consts";
import isArray from "utils/isArray";
import isNullOrUndefOrEmpty from "utils/isNullOrUndefOrEmpty";
import toObject from "utils/toObject";

export const backgroundImageGradient = "linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25))";

/**
 * @typedef {import('preact').JSX.CSSProperties} CSSProperties
 * @typedef {import('types/store').QuestionsData} QuestionsData
 */

/**
 * @typedef {Object} UseBackgroundImageParams
 * @property {boolean} [backgroundImageEnabled] - Whether background images are enabled
 * @property {QuestionsData} [questionsData] - Questions data object
 * @property {number} [questionIndex] - Current question index
 * @property {number} [widgetState] - Current widget state
 */

/**
 * @typedef {Object} UseBackgroundImageReturn
 * @property {boolean} showBackgroundImg - Whether to show the background image
 * @property {string | null | undefined} imgUrl - URL of the background image
 * @property {CSSProperties | undefined} backgroundImageStyles - CSS styles for background image
 */

/**
 * Custom hook for managing background images in the widget
 * @param {UseBackgroundImageParams} params
 * @returns {UseBackgroundImageReturn}
 */
const useBackgroundImage = ({ backgroundImageEnabled = false, questionsData, questionIndex = 0, widgetState = 0 }) => {
  const [imgUrl, setImgUrl] = useState(/** @type {string | null | undefined} */ (undefined));
  const showBackgroundImg =
    backgroundImageEnabled && !isNullOrUndefOrEmpty(imgUrl) && widgetState === WidgetState.Questions;

  useEffect(() => {
    const { questions = [] } = toObject(questionsData);
    setImgUrl(isArray(questions) && questions.length > 0 ? questions[questionIndex]?.imgUrl : undefined);
  }, [questionsData, questionIndex]);

  return {
    showBackgroundImg,
    imgUrl,
    backgroundImageStyles: showBackgroundImg
      ? {
          backgroundSize: "cover",
          backgroundImage: `${backgroundImageGradient}, url(${imgUrl})`,
        }
      : undefined,
  };
};

export default useBackgroundImage;
